import type { SlateBlock } from '@atlas/renderer';
import { Editor, Location, Point, Range, Text } from 'slate';
import getNode from '@atlas/tool/box/slate/getNode';
import sortSelection from '@atlas/tool/box/slate/sortSelection';

export default function locateTextBefore(
    editor: Editor,
    query: string | RegExp,
    at?: Location,
    options?: { requireNoSymbolsBefore?: string },
): Range | null {
    const beginning = at
        ? at instanceof Array
            ? ({ offset: 0, path: at } as Point)
            : 'path' in at
            ? (at as Point)
            : sortSelection(at)[0]
        : (editor.selection && sortSelection(editor.selection)[0]) || null;

    if (!beginning) return null;

    const blockPath = beginning.path.slice(0, -1);

    if (typeof query === 'string' && !query.length) return null;

    const text = Editor.string(editor, {
        anchor: { offset: 0, path: blockPath.concat(0) },
        focus: beginning,
    });

    if (options?.requireNoSymbolsBefore && typeof query !== 'string') {
        const symbol = options.requireNoSymbolsBefore;
        const lastSymbolIndex = text.lastIndexOf(symbol);

        if (lastSymbolIndex > 0) {
            const beforeSymbol = text.slice(lastSymbolIndex - 1, lastSymbolIndex);
            if (/[^\s]/.test(beforeSymbol)) return null;
        }
    }

    if (query instanceof RegExp) {
        const matches = text.match(new RegExp(query.source, 'g'));
        if (!matches) return null;
        query = matches[matches.length - 1];
    }

    const index = text.lastIndexOf(query);
    if (index === -1) return null;

    const block = getNode<SlateBlock>(editor, blockPath);
    if (!block || !('children' in block)) return null;

    let total = 0;
    let start: Point | undefined;
    for (let i = 0; i < block.children.length; i++) {
        const leaf = block.children[i];
        if (!Text.isText(leaf)) continue;

        total += leaf.text.length;
        if (total <= index) continue;

        if (!start) {
            const offset = leaf.text.length - (total - index);
            start = { offset, path: blockPath.concat(i) };
        }

        if (total >= index + query.length) {
            const offset = leaf.text.length - (total - index) + query.length;
            const end = { offset, path: blockPath.concat(i) };
            return { anchor: start, focus: end };
        }
    }

    return null;
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('locateTextBefore', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
